<template>
  <div>
    <h1 class="modal-h1 mb-3">
      <button type="button"
              class="btn btn-back"
              @click="$emit('cancel')"
      />
      Create User
    </h1>
    <form @submit.prevent="create" @keydown="clearError($event)" autocomplete="no">
      <div class="l-bg-white">
        <div class="row mb-3">
          <div class="col-6 pl-1 pr-1">
            <div class="select-wrap">
              <label>First name</label>
              <input v-model="user.first_name"
                     type="text"
                     placeholder="Enter First Name"
                     class="form-control"
              />
            </div>
            <error-field :errors="errors" field="first_name" />
          </div>
          <div class="col-6 pl-1 pr-1">
            <div class="select-wrap">
              <label>Last name</label>
              <input v-model="user.last_name"
                     type="text"
                     placeholder="Enter Last Name"
                     class="form-control"
              />
            </div>
            <error-field :errors="errors" field="last_name" />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-6 pl-1 pr-1">
            <div class="select-wrap">
              <label>Email</label>
              <input v-model="user.email"
                     type="email"
                     placeholder="Enter Email"
                     class="form-control"
              />
            </div>
            <error-field :errors="errors" field="email" />
          </div>
          <div class="col-6 pl-1 pr-1">
            <div class="select-wrap">
              <label>Phone</label>
              <input v-model="user.phone"
                     type="text"
                     class="form-control"
                     @keydown="maskPhone($event)"
                     placeholder="Enter Phone"
                     maxlength="14"
              />
            </div>
            <error-field :errors="errors" field="phone" />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-6 pl-1 pr-1">
            <div class="select-wrap">
              <label>Username</label>
              <input v-model="user.username"
                     type="text"
                     placeholder="Enter Username"
                     class="form-control"
              />
            </div>
            <error-field :errors="errors" field="username" />
          </div>
          <div class="col-6 pl-1 pr-1">
            <div class="d-flex align-items-center pl-2">
              <span class="pr-2">
                Receive automated update emails
              </span>
              <label class="switch mr-2">
                <input type="checkbox"
                       v-model="user.receive_notifications"
                />
                <div class="slider round" />
              </label>
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-12 pl-1 pr-1">
            <div class="select-wrap">
              <label>Carriers</label>
              <key-multiselect v-model="user.carriers"
                               :options="listCarriers"
                               :multiple="true"
                               :clear-on-select="false"
                               :hide-selected="true"
                               :preserve-search="false"
                               label="name"
                               track-by="id"
                               @input="handleSelectAllCarriers"
              >
                <template slot="tag" slot-scope="props">
                  <span class="multiselect__tag">
                    <span>{{ props.option.name }}</span>
                    <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                  </span>
                </template>
              </key-multiselect>
            </div>
            <error-field :errors="errors" field="carrier" />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-12 pl-1 pr-1">
            <div class="select-wrap">
              <label>Company</label>
              <key-multiselect v-model="user.companies"
                               :options="listCompanies"
                               :multiple="true"
                               :clear-on-select="false"
                               :hide-selected="true"
                               :preserve-search="false"
                               label="name"
                               track-by="id"
                               @input="handleSelectAllCompanies"
              >
                <template slot="tag" slot-scope="props">
                  <span class="multiselect__tag">
                    <span>{{ props.option.name }}</span>
                    <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                  </span>
                </template>
              </key-multiselect>
            </div>
            <error-field :errors="errors" field="companies" />
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-12 pl-1 pr-1">
            <div class="select-wrap">
              <label>Role</label>
              <key-multiselect v-model="user.roles"
                               :options="rolesFromCarrier"
                               :multiple="true"
                               :clear-on-select="false"
                               :hide-selected="true"
                               :preserve-search="false"
                               label="name"
                               track-by="id"
              >
                <template slot="tag" slot-scope="props">
                  <span class="multiselect__tag">
                    <span>{{ props.option.name }}</span>
                    <span class="multiselect__tag-icon" @click="props.remove(props.option)" />
                  </span>
                </template>
              </key-multiselect>
            </div>
            <error-field :errors="errors" field="roles" />
          </div>
        </div>
        <div class="row mvb-3">
          <div class="col-12 pl-1 pr-1">
            <div class="select-wrap">
              <label>Timezone</label>
              <simple-select
                v-model="user.timezone"
                :options="timezones"
                placeholder="Select time zone"
              />
            </div>
          </div>
        </div>
        <div class="row mt-5">
          <div class="col">
            <button class="btn btn-primary float-right">Create</button>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapState } from 'vuex';
import toastr from 'toastr';
import tippy from 'tippy.js';
import Errors from '../../classes/Errors';
import ErrorField from '../ErrorField';
import PhoneMask from '../../classes/PhoneMask';

export default {
  components: {
    ErrorField,
  },
  data: () => ({
    user: {
      first_name: '',
      last_name: '',
      username: '',
      roles: [],
      carriers: [],
      companies: [],
      email: '',
      phone: '',
      receive_notifications: false,
    },
    phoneMask: new PhoneMask(),
    errors: new Errors(),
    rolesFromCarrier: [],
    listCarriers: [],
    listCompanies: [],
  }),
  computed: {
    ...mapState({
      roles: state => state.roles.roles,
      companies: state => state.companies.companies.data,
      carriers: state => state.carriers.carriers.data,
      timezones: state => state.timezones.timezones.data,
    }),
    selectedCarriers() {
      return this.user.carriers;
    },
    selectedCompanies() {
      return this.user.companies;
    },
  },
  watch: {
    roles() {
      this.getRolesFromCarriers();
    },
    companies(val) {
      this.listCompanies = [{ id: 'SELECTED_ALL', name: 'Select all companies' }, ...val];
    },
    carriers(val) {
      this.listCarriers = [{ id: 'SELECTED_ALL', name: 'Select all carriers' }, ...val];
    },
  },
  mounted() {
    if (this.roles) {
      this.getRolesFromCarriers();
    }
    this.$nextTick(() => {
      window.$('input').attr('autocomplete', 'no');
    });
    tippy('.popup', {
      placement: 'bottom',
      html: '#myTemplate',
    });
    this.getTimezones();
    this.listCompanies = [{ id: 'SELECTED_ALL', name: 'Select all companies' }, ...this.companies];
    this.listCarriers = [{ id: 'SELECTED_ALL', name: 'Select all carriers' }, ...this.carriers];
  },
  methods: {
    ...mapActions(['getTimezones']),
    create() {
      this.user.phone.replace(/\D/g, '');
      axios
        .post(`clusters/users`, { ...this.user })
        .then(() => {
          toastr.success('Successfully created user');
          this.$emit('update');
          this.$emit('cancel');
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    handleSelectAllCompanies() {
      this.getRolesFromCarriers();
      let hasToSelectAll = false;
      let hastToDeselectAll = false;
      this.selectedCompanies.forEach(companyId => {
        if (companyId === 'SELECTED_ALL') {
          hasToSelectAll = true;
        }
        if (companyId === 'DESELECT_ALL') {
          hastToDeselectAll = true;
        }
      });
      if (hastToDeselectAll) {
        this.$set(this.user, 'companies', []);
        this.listCompanies = [
          { id: 'SELECTED_ALL', name: 'Select all companies' },
          ...this.companies,
        ];
        return;
      }

      if (hasToSelectAll) {
        this.$set(this.user, 'companies', this.companies.map(company => company.id));
        this.listCompanies = [{ id: 'DESELECT_ALL', name: 'Deselect all' }, ...this.companies];
      }
    },
    handleSelectAllCarriers() {
      this.getRolesFromCarriers();
      let hasToSelectAll = false;
      let hastToDeselectAll = false;
      this.selectedCarriers.forEach(carrierId => {
        if (carrierId === 'SELECTED_ALL') {
          hasToSelectAll = true;
        }
        if (carrierId === 'DESELECT_ALL') {
          hastToDeselectAll = true;
        }
      });
      if (hastToDeselectAll) {
        this.$set(this.user, 'carriers', []);
        this.listCarriers = [
          { id: 'SELECTED_ALL', name: 'Select all companies' },
          ...this.carriers,
        ];
      }

      if (hasToSelectAll) {
        this.$set(this.user, 'carriers', this.carriers.map(company => company.id));
        this.listCarriers = [{ id: 'DESELECT_ALL', name: 'Deselect all' }, ...this.carriers];
      }
    },
    clearError(event) {
      this.errors.clear(event.target.name);
    },
    getRolesFromCarriers() {
      const carriers = this.user.carriers.slice();
      if (this.companies) {
        const companies = this.companies.filter(company =>
          this.user.companies.includes(company.id),
        );
        companies.forEach(element => {
          carriers.push(element.carrier_id);
        });
      }
      this.rolesFromCarrier = this.roles.filter(
        role => carriers.includes(role.carrier_id) || !role.carrier_id || role.carrier_id === 0,
      );
    },
    maskPhone(event) {
      this.$nextTick(() => {
        this.$set(
          this.user,
          'phone',
          this.phoneMask.maskedPhone(this.user.phone.replace(/\D/g, '')),
        );
        this.clearError(event);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 18px;
}

.switch input {
  display: none;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: -2px;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: '';
  height: 10px;
  width: 10px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

input:focus + .slider {
  box-shadow: 0 0 1px #101010;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}
.check-field {
  border-bottom: none !important;
}
</style>



// WEBPACK FOOTER //
// src/components/users/UsersCreate.vue